/**
 * Trust Ecosystem — Suite Registry
 * ────────────────────────────────────────────────────────────────
 * Single source of truth for every app in the Trust Ed Techno suite.
 * Used by the SuiteSwitcher and the BloomingFlowerOverlay to render
 * brand-correct icons, colors, and SSO redirects.
 *
 * Strategic Logic (per CEO directive, May 2026):
 *   • LT  (Content & Creative pillar) → [LT, TVD, TD]
 *   • TVD (Content & Creative pillar) → [LT, TVD, TD]
 *   • TD  (Universal Tool)            → [LT, TVD, TD, FT]
 *   • FT  (Enterprise & Financial)    → [FT, TD]
 */
export const APPS = {
  lt: {
    key: 'lt',
    legacyKey: 'lwt',  // for backwards compatibility
    name: 'Learn With Trust',
    short: 'LT',
    descAr: 'منصة الدروس الخصوصية الموثوقة',
    descEn: 'Trusted tutoring marketplace',
    tagline: 'Verified tutors. Trust Score. Live classrooms.',
    href: 'https://learnwithtrust.com',
    iconUrl: '/suite-lt-icon.png',
    brandColor: '#4F46E5',  // indigo-600
    accent: 'indigo',
    pillar: 'creative',
  },
  tvd: {
    key: 'tvd',
    name: 'Trust Visual Director',
    short: 'TVD',
    descAr: 'حوّل كتابك إلى فيلم سينمائي',
    descEn: 'Turn your book into a cinematic film',
    tagline: 'AI cinematic portal — manuscripts to motion.',
    href: 'https://trusttvd.com',
    iconUrl: '/suite-tvd-icon.png',
    brandColor: '#D946EF',  // fuchsia-500
    accent: 'fuchsia',
    pillar: 'creative',
  },
  td: {
    key: 'td',
    name: 'Trust Design',
    short: 'TD',
    descAr: 'انشر كتابك على أمازون',
    descEn: 'Publish your book on Amazon',
    tagline: 'Canva-sleek KDP book editor.',
    href: 'https://td-trustdesign.com',
    iconUrl: '/suite-td-icon.png',
    brandColor: '#10B981',  // emerald-500
    accent: 'emerald',
    pillar: 'universal',
  },
  ft: {
    key: 'ft',
    name: 'Financial Trust',
    short: 'FT',
    descAr: 'منصة الخدمات المالية والمؤسسية',
    descEn: 'Enterprise & financial trust platform',
    tagline: 'Built on Trust. Driven by Compliance.',
    href: 'https://financialtrust.ae',
    iconUrl: '/suite-ft-icon.png',  // optional — falls back to text badge
    brandColor: '#0EA5E9',  // sky-500
    accent: 'sky',
    pillar: 'financial',
  },
};

/**
 * Returns the *ordered* list of apps to show in the switcher for the
 * given current app. The current app is always first (active state).
 */
export function getSuiteFor(currentKey) {
  const k = (currentKey === 'lwt' ? 'lt' : currentKey) || 'lt';
  switch (k) {
    case 'lt':  return [APPS.lt,  APPS.tvd, APPS.td];
    case 'tvd': return [APPS.tvd, APPS.lt,  APPS.td];
    case 'td':  return [APPS.td,  APPS.lt,  APPS.tvd, APPS.ft];
    case 'ft':  return [APPS.ft,  APPS.td];
    default:    return [APPS.lt,  APPS.tvd, APPS.td];
  }
}

/** Targets supported by the LT SSO endpoint (issuing side). */
export const SSO_TARGETS = ['td', 'tvd', 'ft'];
